import React, { useDeferredValue, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaArrowLeft, FaEdit, FaLink } from "react-icons/fa";
import { useAuth } from "context/auth-context";
import adminApi from "services/admin-api";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import {
  AssetGalleryManager,
  DetailSectionSkeleton,
  PageHeader,
  SectionCard,
  StatCard,
  StatsGridSkeleton,
  StatusPill,
} from "components/admin";
import { DataTable } from "components/molecules/table/table";
import RightDrawer from "components/global/RightDrawer";
import { TeamForm } from "components/molecules/forms/team-form/team-form";
import { getTeamsWorkspaceContent } from "pages/app/teams/teams-access";
import { isRecoverableUploadResponseError } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";
import {
  TeamProfileSecondaryButton,
  TeamProfilePrimaryButton,
  TeamProfileSectionActionButton,
  TeamProfileSectionSecondary,
  TeamProfileInfoRow,
  TeamProfileSectionTertiary,
  TeamProfileGridLayout,
  TeamProfileGridLayoutSecondary,
  TeamProfileContentStack,
  TeamProfileGridLayoutTertiary,
  TeamProfileSection,
  TeamProfileThumbnailImage,
  TeamProfilePreviewImage,
  TeamProfileTitleText,
  TeamProfileLabelText,
  TeamProfileMetaText,
  TeamProfileEyebrowText,
  TeamProfileLinkForm,
  TeamProfileLinkFormActions,
  TeamProfileLinkBrowseToggle,
  TeamProfileLinkFormCopy,
  TeamProfileLinkFormInput,
  TeamProfileLinkFormLabel,
  TeamProfileLinkFormSelect,
  TeamProfileLinkSearchResultButton,
  TeamProfileLinkSearchResults,
  TeamProfileLinkSearchResultText,
} from "pages/app/teams/styles/team-profile.page.inline.styles";
const TeamProfilePage: React.FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isLinkPlayerDrawerOpen, setLinkPlayerDrawerOpen] = useState(false);
  const [selectedLinkPlayerId, setSelectedLinkPlayerId] = useState("");
  const [linkPlayerPosition, setLinkPlayerPosition] = useState("");
  const [linkPlayerSearchValue, setLinkPlayerSearchValue] = useState("");
  const [showPlayerBrowseSelect, setShowPlayerBrowseSelect] = useState(false);
  const deferredLinkPlayerSearch = useDeferredValue(linkPlayerSearchValue);
  const { data: team, isLoading } = useQuery({
    queryKey: ["team", id],
    queryFn: () => adminApi.teams.get(id),
    enabled: Boolean(id),
  });
  const { data: players = [] } = useQuery({
    queryKey: ["team-players", id],
    queryFn: () => adminApi.teams.getPlayers(id),
    enabled: Boolean(id),
  });
  const { data: allAthletes = [] } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
    enabled: Boolean(id),
  });
  const { data: galleryItems = [], isLoading: isGalleryLoading } = useQuery({
    queryKey: ["team-gallery", id],
    queryFn: () => adminApi.galleryItems.listTeam(id),
    enabled: Boolean(id),
  });
  const { data: sports = [] } = useQuery({
    queryKey: ["sports"],
    queryFn: () => adminApi.sports.list(),
  });
  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: () => adminApi.countries.list(),
  });
  const availablePlayersToLink = allAthletes.filter(
    (athlete: any) => !players.some((player: any) => String(player.id) === String(athlete.id)),
  );
  const filteredAvailablePlayersToLink = availablePlayersToLink.filter((athlete: any) =>
    [
      athlete.full_name,
      athlete.position,
      athlete.country,
      athlete.primary_team?.name,
      ...(athlete.sport_names || []),
    ]
      .join(" ")
      .toLowerCase()
      .includes(deferredLinkPlayerSearch.trim().toLowerCase()),
  );
  const visiblePlayerLinkSuggestions = filteredAvailablePlayersToLink.slice(0, 6);
  const canManageTeams = hasPortalAction(portalAccess, "teams.manage");
  const canManagePlayers = hasPortalAction(portalAccess, "players.manage");
  const workspaceContent = getTeamsWorkspaceContent(portalAccess, canManageTeams);
  const canManageTeamRoster =
    workspaceContent.showManagementActions && canManageTeams && canManagePlayers;
  const canManageTeamGallery = workspaceContent.showManagementActions && canManageTeams;
  const resetLinkPlayerForm = () => {
    setSelectedLinkPlayerId("");
    setLinkPlayerPosition("");
    setLinkPlayerSearchValue("");
    setShowPlayerBrowseSelect(false);
  };
  const selectPlayerToLink = (athleteToLink: any) => {
    setSelectedLinkPlayerId(String(athleteToLink.id));
    setLinkPlayerPosition(athleteToLink?.position || "");
    setLinkPlayerSearchValue(athleteToLink.full_name || "");
  };
  const updateMutation = useMutation({
    mutationFn: (payload: any) => adminApi.teams.update(id, payload),
    onSuccess: () => {
      toast.success("Team updated.");
      queryClient.invalidateQueries({
        queryKey: ["team", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
      setDrawerOpen(false);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Team updated and image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["team", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["teams"],
        });
        setDrawerOpen(false);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to update this team.");
    },
  });
  const linkPlayerMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLinkPlayerId) {
        throw new Error("Select a player first.");
      }

      const athleteToLink: any = await adminApi.athletes.get(String(selectedLinkPlayerId));
      const existingTeamAssignments = Array.isArray(athleteToLink?.team_names_and_positions)
        ? athleteToLink.team_names_and_positions
        : [];
      const existingTeamIds = existingTeamAssignments
        .map((assignment: any) => String(assignment.id))
        .filter((teamId: string) => teamId);
      const existingPositionsByTeamId = new Map(
        existingTeamAssignments.map((assignment: any) => [
          String(assignment.id),
          assignment.position || "",
        ]),
      );
      const normalizedPosition =
        linkPlayerPosition.trim() || athleteToLink?.position?.trim?.() || "";

      if (!normalizedPosition) {
        throw new Error("Add a position for this player first.");
      }

      const nextTeamIds = Array.from(new Set([...existingTeamIds, String(id)]));
      const nextPositions = nextTeamIds.map(
        (teamId) => existingPositionsByTeamId.get(String(teamId)) || normalizedPosition,
      );

      return adminApi.athletes.update(String(selectedLinkPlayerId), {
        position: normalizedPosition,
        team_ids: nextTeamIds,
        positions: nextPositions,
      });
    },
    onSuccess: () => {
      toast.success("Player linked to team.");
      queryClient.invalidateQueries({
        queryKey: ["team-players", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
      resetLinkPlayerForm();
      setLinkPlayerDrawerOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || error?.message || "Unable to link this player.");
    },
  });
  const unlinkPlayerMutation = useMutation({
    mutationFn: async (playerToUnlink: any) => {
      const teamAssignments = Array.isArray(playerToUnlink?.team_names_and_positions)
        ? playerToUnlink.team_names_and_positions
        : [];
      const remainingAssignments = teamAssignments.filter(
        (assignment: any) => String(assignment.id) !== String(id),
      );

      return adminApi.athletes.update(String(playerToUnlink.id), {
        team_ids: remainingAssignments.map((assignment: any) => String(assignment.id)),
        positions: remainingAssignments.map(
          (assignment: any) => assignment.position || playerToUnlink.position || "",
        ),
      });
    },
    onSuccess: () => {
      toast.success("Player unlinked from team.");
      queryClient.invalidateQueries({
        queryKey: ["team-players", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to unlink this player.");
    },
  });
  return (
    <AppLayout route="/teams">
      <PageHeader
        title={team?.name || "Team profile"}
        description="Track a squad’s core metadata and see every athlete currently linked to it."
        crumbs={[
          {
            label: "Dashboard",
            to: "/dashboard",
          },
          {
            label: "Teams",
            to: "/teams",
          },
          {
            label: team?.name || "Team",
          },
        ]}
        actions={
          <>
            <TeamProfileSecondaryButton onClick={() => history.push("/teams")} type="button">
              <FaArrowLeft size={12} />
              Back
            </TeamProfileSecondaryButton>
            {workspaceContent.showManagementActions && canManageTeams ? (
              <TeamProfilePrimaryButton onClick={() => setDrawerOpen(true)} type="button">
                <FaEdit size={12} />
                Edit team
              </TeamProfilePrimaryButton>
            ) : null}
          </>
        }
      />

      {isLoading ? (
        <StatsGridSkeleton />
      ) : (
        <TeamProfileGridLayout>
          <StatCard label="Sport" value={team?.sport || "Unassigned"} />
          <StatCard label="Country" value={team?.country || "Unknown"} />
          <StatCard label="Players" value={players.length || team?.player_count || 0} />
          <StatCard label="Status" value={<StatusPill value={team?.status} />} />
        </TeamProfileGridLayout>
      )}

      <TeamProfileGridLayoutSecondary>
        <SectionCard subtitle="Core team identity and placement" title="Overview">
          {isLoading ? (
            <DetailSectionSkeleton imageClassName="h-32 w-32 max-w-full sm:h-44 sm:w-44" />
          ) : (
            <TeamProfileContentStack>
              <TeamProfilePreviewImage
                alt={team?.name || "Team"}
                fallbackLabel={team?.name}
                src={team?.image_url}
                variant="team"
              />
              <TeamProfileGridLayoutTertiary>
                <TeamProfileSection>
                  <TeamProfileEyebrowText>Sport</TeamProfileEyebrowText>
                  <TeamProfileTitleText>{team?.sport || "Unassigned"}</TeamProfileTitleText>
                </TeamProfileSection>
                <TeamProfileSection>
                  <TeamProfileEyebrowText>Level</TeamProfileEyebrowText>
                  <TeamProfileTitleText>{team?.level || "Unspecified"}</TeamProfileTitleText>
                </TeamProfileSection>
                <TeamProfileSection>
                  <TeamProfileEyebrowText>Country</TeamProfileEyebrowText>
                  <TeamProfileTitleText>{team?.country || "Unknown"}</TeamProfileTitleText>
                </TeamProfileSection>
                <TeamProfileSection>
                  <TeamProfileEyebrowText>Status</TeamProfileEyebrowText>
                  <TeamProfileSectionSecondary>
                    <StatusPill value={team?.status} />
                  </TeamProfileSectionSecondary>
                </TeamProfileSection>
              </TeamProfileGridLayoutTertiary>
            </TeamProfileContentStack>
          )}
        </SectionCard>

        <SectionCard
          action={
            canManageTeamRoster ? (
              <TeamProfileSectionActionButton
                disabled={!availablePlayersToLink.length}
                onClick={() => setLinkPlayerDrawerOpen(true)}
                type="button"
              >
                <FaLink size={12} />
                Link player
              </TeamProfileSectionActionButton>
            ) : undefined
          }
          subtitle={workspaceContent.rosterSubtitle}
          title="Roster"
        >
          <DataTable
            canEdit={false}
            canRemove={canManageTeamRoster}
            columns={[
              {
                label: "Athlete",
                accessor: (row: any) => (
                  <TeamProfileInfoRow>
                    <TeamProfileThumbnailImage
                      alt={row.full_name || "Athlete"}
                      fallbackLabel={row.full_name}
                      src={row.avatar_url || row.image}
                      variant="athlete"
                    />
                    <div>
                      <TeamProfileLabelText>{row.full_name}</TeamProfileLabelText>
                      <TeamProfileMetaText>
                        {row.position || "Position pending"}
                      </TeamProfileMetaText>
                    </div>
                  </TeamProfileInfoRow>
                ),
              },
              {
                label: "Country",
                accessor: (row: any) => row.country || "Unknown",
              },
              {
                label: "Followers",
                accessor: (row: any) => row.follower_count ?? 0,
              },
            ]}
            data={players}
            hideNav
            onRemove={
              canManageTeamRoster
                ? (athleteId, row) => {
                    const athlete = row as any;

                    if (
                      !window.confirm(
                        `Unlink ${athlete?.full_name || "this player"} from ${team?.name || "this team"}?`,
                      )
                    ) {
                      return;
                    }

                    unlinkPlayerMutation.mutate(athlete || { id: athleteId });
                  }
                : undefined
            }
            onView={(athleteId) => history.push(`/athletes/${athleteId}`)}
          />
        </SectionCard>
      </TeamProfileGridLayoutSecondary>

      <TeamProfileSectionTertiary>
        <AssetGalleryManager
          canManage={canManageTeamGallery && Boolean(id)}
          createLabel="Add team asset"
          emptyDescription="Team media will appear here once gallery items are added."
          invalidateKeys={[["team-gallery", id]]}
          isLoading={isGalleryLoading}
          items={galleryItems}
          onCreate={(payload) => adminApi.galleryItems.createTeam(id, payload)}
          onRemove={(item) => adminApi.galleryItems.remove(String(item.id))}
          onUpdate={(item, payload) => adminApi.galleryItems.update(String(item.id), payload)}
          subtitle="Use the new team gallery API for squad imagery, posters, matchday media, and highlight clips."
          title="Team Gallery"
        />
      </TeamProfileSectionTertiary>

      <RightDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} title="Edit team">
        <TeamForm
          countries={countries.map((country: any) => ({
            label: country.name,
            value: country.name,
          }))}
          initialValues={{
            name: team?.name || "",
            sport: team?.sport || "",
            country: team?.country || "",
            level: team?.level || "",
            image: team?.image_url || "",
            status: team?.status || "active",
          }}
          onCancel={() => setDrawerOpen(false)}
          onSubmit={(values) => updateMutation.mutate(values)}
          sports={sports.map((sport: any) => ({
            label: sport.name,
            value: sport.name,
          }))}
        />
      </RightDrawer>

      <RightDrawer
        isOpen={isLinkPlayerDrawerOpen}
        onClose={() => {
          setLinkPlayerDrawerOpen(false);
          resetLinkPlayerForm();
        }}
        title="Link player"
      >
        <TeamProfileLinkForm
          onSubmit={(event) => {
            event.preventDefault();

            if (!selectedLinkPlayerId) {
              toast.error("Select a player first.");
              return;
            }

            linkPlayerMutation.mutate();
          }}
        >
          <TeamProfileLinkFormCopy>
            <TeamProfileMetaText>
              Link an existing athlete to {team?.name || "this team"} and keep their other team
              memberships intact.
            </TeamProfileMetaText>
          </TeamProfileLinkFormCopy>

          <div className="space-y-2">
            <TeamProfileLinkFormLabel htmlFor="team-player-search-input">
              Choose player
            </TeamProfileLinkFormLabel>
            <TeamProfileLinkFormInput
              id="team-player-search-input"
              onChange={(event) => {
                setLinkPlayerSearchValue(event.target.value);
                setSelectedLinkPlayerId("");
              }}
              placeholder="Search existing players"
              type="search"
              value={linkPlayerSearchValue}
            />
            {deferredLinkPlayerSearch.trim() ? (
              visiblePlayerLinkSuggestions.length ? (
                <TeamProfileLinkSearchResults>
                  {visiblePlayerLinkSuggestions.map((athlete: any) => (
                    <TeamProfileLinkSearchResultButton
                      key={athlete.id}
                      onClick={() => selectPlayerToLink(athlete)}
                      type="button"
                    >
                      <TeamProfileLinkSearchResultText>
                        <TeamProfileLabelText>
                          {athlete.full_name || "Unnamed athlete"}
                        </TeamProfileLabelText>
                        <TeamProfileMetaText>
                          {[athlete.position, athlete.primary_team?.name, athlete.country]
                            .filter(Boolean)
                            .join(" | ") || "No player details yet"}
                        </TeamProfileMetaText>
                      </TeamProfileLinkSearchResultText>
                      {String(selectedLinkPlayerId) === String(athlete.id) ? (
                        <TeamProfileMetaText>Selected</TeamProfileMetaText>
                      ) : null}
                    </TeamProfileLinkSearchResultButton>
                  ))}
                </TeamProfileLinkSearchResults>
              ) : (
                <TeamProfileMetaText>No players match that search yet.</TeamProfileMetaText>
              )
            ) : null}
            <TeamProfileLinkBrowseToggle
              onClick={() => setShowPlayerBrowseSelect((currentValue) => !currentValue)}
              type="button"
            >
              {showPlayerBrowseSelect ? "Hide browse list" : "Browse all players"}
            </TeamProfileLinkBrowseToggle>
            {showPlayerBrowseSelect ? (
              <TeamProfileLinkFormSelect
                id="team-player-link-select"
                onChange={(event) => {
                  const nextPlayerId = event.target.value;
                  const athleteToLink = availablePlayersToLink.find(
                    (athlete: any) => String(athlete.id) === String(nextPlayerId),
                  );

                  setSelectedLinkPlayerId(nextPlayerId);

                  if (athleteToLink) {
                    selectPlayerToLink(athleteToLink);
                  }
                }}
                value={selectedLinkPlayerId}
              >
                <option value="">Choose a player</option>
                {availablePlayersToLink.map((athlete: any) => (
                  <option key={athlete.id} value={athlete.id}>
                    {athlete.full_name || "Unnamed athlete"}
                    {athlete.position ? ` | ${athlete.position}` : ""}
                    {athlete.primary_team?.name ? ` | ${athlete.primary_team.name}` : ""}
                  </option>
                ))}
              </TeamProfileLinkFormSelect>
            ) : null}
          </div>

          <div className="space-y-2">
            <TeamProfileLinkFormLabel htmlFor="team-player-position-input">
              Position
            </TeamProfileLinkFormLabel>
            <TeamProfileLinkFormInput
              id="team-player-position-input"
              onChange={(event) => setLinkPlayerPosition(event.target.value)}
              placeholder="Forward"
              type="text"
              value={linkPlayerPosition}
            />
          </div>

          <TeamProfileLinkFormActions>
            <TeamProfileSecondaryButton
              onClick={() => {
                setLinkPlayerDrawerOpen(false);
                resetLinkPlayerForm();
              }}
              type="button"
            >
              Cancel
            </TeamProfileSecondaryButton>
            <TeamProfilePrimaryButton disabled={linkPlayerMutation.isPending} type="submit">
              {linkPlayerMutation.isPending ? "Linking..." : "Link player"}
            </TeamProfilePrimaryButton>
          </TeamProfileLinkFormActions>
        </TeamProfileLinkForm>
      </RightDrawer>
    </AppLayout>
  );
};
export default TeamProfilePage;
